import {
  Box,
  Button,
  Flex,
  Icon,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Avatar,
  HStack,
  VStack,
  Badge,
  useToast,
  Spinner,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
    Checkbox,
} from '@chakra-ui/react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import * as React from 'react';
import Card from 'components/card/Card';
import CustomSelect from 'components/select/CustomSelect';
import { FaCrown, FaAward, FaSeedling, FaUser } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useChangeSubscriptionMutation, useGetSubscriptionsQuery } from 'api/subscriptionsSlice';
import { PageShimmer } from 'components/ui/Shimmer';

const columnHelper = createColumnHelper();

const SubscriptionPlans = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [sorting, setSorting] = React.useState([]);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [perPage, setPerPage] = React.useState(15);

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  // API query parameters
  const queryParams = React.useMemo(() => {
    const params = {
      page: currentPage,
      per_page: perPage,
    };
    
    return params;
  }, [currentPage, perPage]);

  // API hooks
  const { data: subscriptionsResponse, isLoading, isError, refetch } = useGetSubscriptionsQuery(queryParams, { refetchOnMountOrArgChange: true });
  const [changeSubscription, { isLoading: isChanging }] = useChangeSubscriptionMutation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedUser, setSelectedUser] = React.useState(null);
  const [nextPlan, setNextPlan] = React.useState('skilled');
  const [applyImmediately, setApplyImmediately] = React.useState(true);

  const subscriptionsData = subscriptionsResponse?.data || [];
  const pagination = subscriptionsResponse?.pagination || null;

  // Effect to refetch when pagination changes
  React.useEffect(() => {
    refetch();
  }, [currentPage, perPage, refetch]);

  // Helper functions for plan data
  const getPlanConfig = (planName) => {
    const plans = {
      'rookie': {
        name: 'Rookie',
        icon: FaSeedling,
        colorScheme: 'green'
      },
      'skilled': {
        name: 'Skilled',
        icon: FaAward,
        colorScheme: 'blue'
      },
      'master': {
        name: 'Master',
        icon: FaCrown,
        colorScheme: 'purple'
      }
    };
    return plans[String(planName || '').toLowerCase()] || { name: planName || 'Unknown', icon: FaUser, colorScheme: 'gray' };
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'active': 'green',
      'inactive': 'red',
      'suspended': 'orange'
    };
    return statusColors[status] || 'gray';
  };

  const planRank = (plan) => ({ rookie: 0, skilled: 1, master: 2 }[String(plan || '').toLowerCase()] ?? 0);

  const handleChangePlan = async () => {
    if (!selectedUser?.user_id || !nextPlan) return;
    try {
      const response = await changeSubscription({
        userId: selectedUser.user_id,
        plan: nextPlan,
        immediate: applyImmediately,
      }).unwrap();
      toast({
        title: response?.message || 'Plan updated',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onClose();
      refetch();
    } catch (error) {
      toast({
        title: 'Could not change plan',
        description: error?.data?.message || 'Please try again',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const columns = [
    columnHelper.accessor('user_id', {
      id: 'user',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          User
        </Text>
      ),
      cell: (info) => (
        <HStack spacing={3}>
          <Avatar
            size="sm"
            name={info.row.original.full_name}
            bg="blue.500"
            color="white"
          />
          <VStack align="start" spacing={0}>
            <Text color={textColor} fontWeight="bold" fontSize="sm">
              {info.row.original.full_name}
            </Text>
            <Text color="gray.500" fontSize="xs">
              {info.row.original.email}
            </Text>
          </VStack>
        </HStack>
      ),
    }),
    columnHelper.accessor('plan', {
      id: 'plan',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          Plan
        </Text>
      ),
      cell: (info) => {
        const planConfig = getPlanConfig(info.getValue());
        return (
          <HStack spacing={2}>
            <Icon as={planConfig.icon} color={`${planConfig.colorScheme}.500`} boxSize="16px" />
            <VStack align="start" spacing={0}>
              <Text color={textColor} fontWeight="bold" fontSize="sm">
                {planConfig.name}
              </Text>
              <Text color="gray.500" fontSize="xs">
                {info.row.original.interval ? `${info.row.original.interval}ly` : 'No interval'}
              </Text>
            </VStack>
          </HStack>
        );
      },
    }),
    columnHelper.accessor('started_at', {
      id: 'started_at',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          Started At
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm">
          {info.getValue() ? formatDate(info.getValue()) : 'Not started'}
        </Text>
      ),
    }),
    columnHelper.accessor('ends_at', {
      id: 'ends_at',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          Ends At
        </Text>
      ),
      cell: (info) => {
        const date = info.getValue();
        if (!date) {
          return (
            <Text color="gray.500" fontSize="sm">
              No end date
            </Text>
          );
        }
        return (
            <Text color={textColor} fontSize="sm">
            {formatDate(date)}
              </Text>
        );
      },
    }),
    columnHelper.accessor('is_active', {
      id: 'is_active',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          Subscription Status
        </Text>
      ),
      cell: (info) => (
        <Badge 
          colorScheme={info.getValue() ? 'green' : 'red'}
          px="2"
          py="1"
          borderRadius="full"
          fontSize="xs"
        >
          {info.getValue() ? 'Active' : 'Inactive'}
        </Badge>
      ),
    }),
    columnHelper.accessor('account_status', {
      id: 'account_status',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          Account Status
        </Text>
      ),
      cell: (info) => (
        <Badge 
          colorScheme={getStatusColor(info.getValue())}
          px="2"
          py="1"
          borderRadius="full"
          fontSize="xs"
          textTransform="capitalize"
        >
          {info.getValue()}
        </Badge>
      ),
    }),
    columnHelper.display({
      id: 'actions',
      header: () => (
        <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
          Actions
        </Text>
      ),
      cell: (info) => (
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setSelectedUser(info.row.original);
            setNextPlan(info.row.original.plan === 'master' ? 'skilled' : 'master');
            setApplyImmediately(true);
            onOpen();
          }}
        >
          Change plan
        </Button>
      ),
    }),
  ];

  const table = useReactTable({
    data: subscriptionsData,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });



  // Loading state
  if (isLoading) {
    return (
      <Box className="nr-page">
        <PageShimmer variant="table" />
      </Box>
    );
  }

  // Error state
  if (isError) {
    return (
      <Box className="nr-page">
        <Card
          flexDirection="column"
          w="100%"
          px="0px"
          overflowX={{ sm: 'scroll', lg: 'hidden' }}
        >
          <Flex justify="center" align="center" h="200px">
            <Text color="red.500">Error loading subscriptions. Please try again.</Text>
          </Flex>
        </Card>
      </Box>
    );
  }

  return (
    <Box className="nr-page">
      <Card
        flexDirection="column"
          w="100%"
        px="0px"
          overflowX={{ sm: 'scroll', lg: 'hidden' }}
        >
          <Flex
          px={{ base: "16px", md: "25px" }}
          mb="8px"
          direction={{ base: "column", md: "row" }}
          justifyContent="space-between"
          align={{ base: "stretch", md: "center" }}
          gap={{ base: 4, md: 0 }}
        >
          <Text
            color={textColor}
            fontSize="22px"
            fontWeight="700"
            lineHeight="100%"
          >
            Subscriptions Management
            </Text>
          </Flex>

        <Box>
          <Table variant="simple" color="gray.500" mb="24px" mt="12px">
              <Thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <Th
                        key={header.id}
                        colSpan={header.colSpan}
                        pe="10px"
                        borderColor={borderColor}
                        cursor="pointer"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <Flex
                          justifyContent="space-between"
                          align="center"
                          fontSize={{ sm: '10px', lg: '12px' }}
                          color="gray.400"
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          {{
                            asc: ' 🔼',
                            desc: ' 🔽',
                          }[header.column.getIsSorted()] ?? null}
                        </Flex>
                      </Th>
                    );
                  })}
                  </Tr>
                ))}
              </Thead>
              <Tbody>
              {table.getRowModel().rows.map((row) => {
                return (
                  <Tr key={row.id}>
                    {row.getVisibleCells().map((cell) => {
                      return (
                      <Td
                        key={cell.id}
                        fontSize={{ sm: '14px' }}
                        minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                        borderColor="transparent"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                            cell.getContext(),
                        )}
                      </Td>
                      );
                    })}
                  </Tr>
                );
              })}
              </Tbody>
            </Table>
          </Box>

        {/* Pagination Controls */}
        {pagination && (
          <Flex
            px={{ base: "16px", md: "25px" }}
            py="20px"
            justify="space-between"
            align="center"
            borderTop="1px solid"
            borderColor={borderColor}
            direction={{ base: "column", md: "row" }}
            gap={{ base: 4, md: 0 }}
          >
            {/* Page Info */}
            <Text color={textColor} fontSize="sm">
              Showing {pagination.from || 0} to {pagination.to || 0} of {pagination.total || 0} results
            </Text>

            {/* Pagination Controls */}
            <HStack spacing="3">
              {/* Page Size Selector */}
              <HStack spacing="2">
                <Text fontSize="sm" color={textColor}>
                  Show:
                </Text>
                <CustomSelect
                  size="sm"
                  value={perPage}
                  onChange={(e) => {
                    setPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  style={{
                    padding: '4px 8px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                >
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </CustomSelect>
              </HStack>

              {/* Page Navigation */}
              <HStack spacing="2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  isDisabled={currentPage <= 1}
                >
                  Previous
                </Button>

                {/* Page Numbers */}
                {Array.from({ length: Math.min(5, pagination.last_page || 1) }, (_, i) => {
                  const pageNum = i + 1;
                  const isCurrentPage = pageNum === currentPage;
                  
                  return (
                    <Button
                      key={pageNum}
                      size="sm"
                      variant={isCurrentPage ? "solid" : "outline"}
                      colorScheme={isCurrentPage ? "brand" : "gray"}
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  );
                })}

                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  isDisabled={currentPage >= (pagination.last_page || 1)}
                >
                  Next
                </Button>
              </HStack>
            </HStack>
          </Flex>
        )}

        {/* No data message */}
        {(!subscriptionsData || subscriptionsData.length === 0) && !isLoading && (
          <Flex
            px={{ base: "16px", md: "25px" }}
            py="40px"
            justify="center"
            align="center"
          >
            <Text color={textColor} fontSize="md">
              No subscriptions found.
            </Text>
        </Flex>
        )}
      </Card>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Change plan</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={3} fontSize="sm" color="gray.500">
              {selectedUser?.full_name} is on {getPlanConfig(selectedUser?.plan).name}.
            </Text>
            <CustomSelect value={nextPlan} onChange={(e) => setNextPlan(e.target.value)} mb={3}>
              <option value="rookie">Rookie</option>
              <option value="skilled">Skilled</option>
              <option value="master">Master</option>
            </CustomSelect>
            <Text fontSize="sm" mb={3}>
              {planRank(nextPlan) > planRank(selectedUser?.plan)
                ? 'Upgrade — applies immediately by default.'
                : planRank(nextPlan) < planRank(selectedUser?.plan)
                  ? 'Downgrade — uncheck to wait until the current period ends.'
                  : 'Same plan. This will renew the current subscription.'}
            </Text>
            <Checkbox isChecked={applyImmediately} onChange={(e) => setApplyImmediately(e.target.checked)}>
              Apply immediately
            </Checkbox>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button colorScheme="blue" isLoading={isChanging} onClick={handleChangePlan}>
              Save change
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default SubscriptionPlans;